/* eslint-disable @typescript-eslint/no-namespace */
import { ThreeEvent } from '@react-three/fiber';
import { forwardRef, FunctionComponent, Suspense, useEffect, useMemo, useRef } from 'react';
import { Color, DoubleSide, Group, Intersection, Object3D, Object3DEventMap, Raycaster, ShaderLibShader, Side, Texture, WebGLRenderer } from 'three';
import { FONT_TYPES, IChromaKey, IFontTypes, ITextAlignment, ITuple3, ITuple4 } from '../../component-data-structure';
import DreiText, { IOnTextSync } from '../DreiText';
import { CurvedEntityGeometry } from './CurvedEntityGeometry';
import { getRadiusFromCurvature } from './gizmoUtil';
import { MeshChromaKeyMaterial } from './MeshChromaKeyMaterial';
import { AlphaMaterial } from '../customMaterials';
import CurvedEntityMaterial from './CurvedEntityMaterial';

const isSafari = (navigator.userAgent.indexOf('Safari') != -1 || navigator.userAgent.includes('AppleWebKit')) && navigator.userAgent.indexOf('Chrome') == -1;

interface ICurvedEntityText {
	value: string;
	textAlignment: ITextAlignment;
	fontSize: number;
	fontFamily: IFontTypes;
	fontRgba: ITuple4;
	side?: Side;
	preventPolygonOffset?: boolean;
}

interface ICurvedEntityBorder {
	borderSize: number;
	borderRgba: ITuple4;
}

interface IParentProps {
	position?: ITuple3;
	scale: ITuple3;
	opacity: number;
	renderOrder: number;
	cornerRadius: number;
	curvature: number;
	colorRgba?: ITuple4;
	imageTexture?: Texture;
	videoTexture?: Texture;
	isVideo?: boolean;
	hasAlpha?: boolean;
	visible?: boolean;
	border?: ICurvedEntityBorder;
	text?: ICurvedEntityText;
	stackedAlpha?: boolean;
	name?: string;
	side?: Side;
	isTextScaleLocked?: boolean;
	castShadow?: boolean;
	receiveShadow?: boolean;
    depthTest?: boolean;
	raycast?: (_: Raycaster, intersects: Intersection<Object3D<Object3DEventMap>>[]) => void;
	onPointerDown?: (e: ThreeEvent<PointerEvent>) => unknown;
	onPointerUp?: (e: ThreeEvent<PointerEvent>) => unknown;
	onDoubleClick?: (e: ThreeEvent<MouseEvent>) => unknown;
	chromaKey?: IChromaKey;
	onTextSync?: IOnTextSync;
	onMount?: () => unknown;
}

const TEXT_ENTITY_PADDING = 0.027;

const getEntityTextPos = (text: ICurvedEntityText, scale: ITuple3) => {
	const { textAlignment } = text;
	let xPos: number;
	let anchorXPos: 'left' | 'right' | 'center' | undefined;
	switch (textAlignment) {
		case ITextAlignment.left: {
			xPos = -scale[0] / 2 + TEXT_ENTITY_PADDING;
			anchorXPos = 'left';
			break;
		}
		case ITextAlignment.right: {
			xPos = scale[0] / 2 - TEXT_ENTITY_PADDING;
			anchorXPos = 'right';
			break;
		}
		default: {
			xPos = 0;
			anchorXPos = 'center';
		}
	}
	const textPos = [xPos, 0, 0] as ITuple3;
	return { textPos, anchorXPos };
};

const CurvedEntity = forwardRef<Object3D, IParentProps>(
	(
		{
			scale,
			visible = true,
			imageTexture,
			videoTexture,
			position,
			cornerRadius = 0,
			opacity = 1,
			colorRgba,
			curvature,
			renderOrder = 1,
			hasAlpha = false,
			stackedAlpha = false,
			raycast,
			text,
			isTextScaleLocked,
			border,
			name = 'Curved Entity',
			side = DoubleSide,
			onPointerDown,
			onPointerUp,
			onDoubleClick,
			chromaKey,
			castShadow = false,
			receiveShadow = false,
            depthTest = true,
			onTextSync,
			onMount
		},
		ref
	) => {
		// All cases
		const width = scale[0];
		const widthSegments = Math.floor(Math.abs(width) * 80);
		const height = scale[1];
		const radius = getRadiusFromCurvature(curvature, width) * Math.sign(curvature);
		const innerColor = !!colorRgba ? new Color(`rgb(${colorRgba[0]}, ${colorRgba[1]}, ${colorRgba[2]})`) : undefined;
		const textWrapperGroupRef = useRef<Group>(null);
		const prevXScale = useRef<number>(0.32);
		const hasRunRef = useRef(false);
		const outerRef = useRef<Object3D>(null);
		//const initialScaleRef = useRef(1/scale[0]);

		const onBeforeCompile = (shader: ShaderLibShader) => {
			if (!isSafari) return;
			shader.fragmentShader = shader.fragmentShader.replace(
				'#include <map_fragment>',
				`
			#ifdef USE_MAP
			
				vec4 sampledDiffuseColor = texture2D( map, vMapUv );
			
				// inline sRGB decode
				sampledDiffuseColor = vec4( mix( pow( sampledDiffuseColor.rgb * 0.9478672986 + vec3( 0.0521327014 ), vec3( 2.4 ) ), sampledDiffuseColor.rgb * 0.0773993808, vec3( lessThanEqual( sampledDiffuseColor.rgb, vec3( 0.04045 ) ) ) ), sampledDiffuseColor.a );
		
				diffuseColor *= sampledDiffuseColor;
		
			#endif
		
			`
			);
		};

		useEffect(() => {
			onMount?.();
		}, [])

		useEffect(() => {
			if (!isTextScaleLocked) return;
			prevXScale.current = scale[0];
		}, [isTextScaleLocked]);

		useEffect(() => {
			hasRunRef.current = true;
			if (!isTextScaleLocked) return;
			const ratio = Math.abs(scale[0] / prevXScale.current);

			// if (ratio < 0) return;

			// console.log('adjust', ratio)
			prevXScale.current = scale[0];
			textWrapperGroupRef.current?.scale.multiplyScalar(ratio);
		}, [scale[0]]);

		// Border related
		const borderColor = border?.borderRgba ? new Color(`rgb(${border?.borderRgba[0]}, ${border?.borderRgba[1]}, ${border?.borderRgba[2]})`) : undefined;

		// Text related
		const fontColor = text?.fontRgba ? new Color(`rgb(${text?.fontRgba[0]}, ${text?.fontRgba[1]}, ${text?.fontRgba[2]})`) : undefined;
		const filteredArray = text ? FONT_TYPES.filter((font) => font.fontFamily === text.fontFamily) : [];
		const { textPos, anchorXPos } = text ? getEntityTextPos(text, scale) : { textPos: [0, 0, 0] as ITuple3, anchorXPos: undefined };

		// Memoise geometries
		const curvedEntityGeometry = useMemo(() => {
			if (width === 0 || height === 0 || widthSegments === 0) return null;
			return <CurvedEntityGeometry args={[width, height, widthSegments, cornerRadius, curvature]} />;
		}, [width, height, cornerRadius, curvature, widthSegments]);

		const outerGeometry = useMemo(() => {
			if (width === 0 || height === 0 || widthSegments === 0) return null;
			return border && border.borderSize > 0 ? <CurvedEntityGeometry args={[width, height, widthSegments, cornerRadius, curvature, border.borderSize]} /> : null;
		}, [width, height, cornerRadius, curvature, border, border?.borderSize, widthSegments]);

		if (typeof curvedEntityGeometry === null) return null;
		if (width === 0 || height === 0 || widthSegments === 0) return null;

		return (
			<Suspense fallback={null}>
				<object3D name={name} ref={ref} position={position} raycast={raycast} visible={visible} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onDoubleClick={onDoubleClick}>
					{/*** INNER ***/}
					{opacity === 1.0 && !hasAlpha && (
						<mesh castShadow={castShadow} raycast={raycast} renderOrder={1}>
							{curvedEntityGeometry}
							<meshBasicMaterial colorWrite={false} polygonOffset={true} polygonOffsetUnits={10} polygonOffsetFactor={10} depthWrite={true} side={side} visible={false} />
						</mesh>
					)}
					{receiveShadow && (
						<mesh receiveShadow renderOrder={renderOrder + 6} raycast={raycast}>
							{curvedEntityGeometry}
							<shadowMaterial opacity={0.2} polygonOffset polygonOffsetFactor={25} polygonOffsetUnits={10} />
						</mesh>
					)}
					{/* TODO - this is temporary, while working on ChromaKey */}
					<mesh castShadow={castShadow} renderOrder={renderOrder + 3} raycast={raycast}>
						{curvedEntityGeometry}
						{/* TODO: change how we handle videoTexture here. For now required to not use a clone for stacked alpha videos as they appear to dark otherwise */}
						<CurvedEntityMaterial
							key={imageTexture ? 'img' : 'vid'}
							onBeforeCompile={onBeforeCompile}
							hasAlpha={hasAlpha}
							stackedAlpha={stackedAlpha}
							opacity={opacity}
							side={side}
                            depthTest={depthTest}
							videoTexture={stackedAlpha ? videoTexture : videoTexture?.clone()}
							imageTexture={imageTexture?.clone()}
							innerColor={innerColor}
							chromaKey={chromaKey}
						/>
					</mesh>

					{/*** TEXT ***/}
					{text && (
						<group ref={textWrapperGroupRef}>
							<DreiText
								raycast={raycast}
								key={'btn_text'}
								color={fontColor}
								alpha={text.fontRgba ? text.fontRgba[3] : 1}
								fontSize={text.fontSize}
								position={textPos}
								url={filteredArray[0]?.url}
								textAlign={text.textAlignment}
								fontFamily={filteredArray[0]?.fontFamily}
								anchorY={'middle'} // always middle
								anchorX={anchorXPos} // always left side at btn x center
								maxWidth={Math.abs(scale[0])}
								renderOrder={renderOrder + 5}
                                depthTest={depthTest}
								depthWrite={false}
								curveRadius={radius}
								side={typeof text.side !== 'undefined' ? text.side : DoubleSide}
								curvature={curvature}
								//onTextSync={_onTextSync}
								preventPolygonOffset={!!text.preventPolygonOffset}
							>
								{text.value}
							</DreiText>
						</group>
					)}

					{/*** OUTER ***/}
					{border && border.borderSize > 0 && (
						<mesh castShadow={castShadow} renderOrder={renderOrder + 2} raycast={raycast}>
							{outerGeometry}
							<meshBasicMaterial color={borderColor} side={side} opacity={border.borderRgba[3]} transparent={true} wireframe={false} depthTest={depthTest} depthWrite={false} />
						</mesh>
					)}
				</object3D>
			</Suspense>
		);
	}
);

CurvedEntity.displayName = 'curved-entity';
export default CurvedEntity;
